<script setup>
/* global axios */
import { onBeforeMount, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TdfAPI from 'dashboard/api/tdfConfig';

const { t } = useI18n();

const bookings = ref([]);
const isLoading = ref(false);
const mapDays = ref(7);

const fetchBookings = async () => {
  isLoading.value = true;
  try {
    const { data } = await TdfAPI.bookings();
    bookings.value = data;
  } catch {
    bookings.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const statusLabel = status => {
  const labels = {
    budget: 'Orçamento',
    confirmed: 'Confirmada',
    checked_in: 'Check-in',
    checked_out: 'Check-out',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
};

const statusClass = status => {
  const classes = {
    budget: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    checked_in: 'bg-blue-100 text-blue-700',
    checked_out: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-500';
};

const mapDates = computed(() => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < mapDays.value; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    dates.push(d);
  }
  return dates;
});

const bookingsInRange = computed(() => {
  if (!bookings.value.length) return [];
  const start = mapDates.value[0];
  const end = mapDates.value[mapDates.value.length - 1];
  return bookings.value.filter(b => {
    const checkIn = new Date(b.check_in);
    const checkOut = new Date(b.check_out);
    return checkIn <= end && checkOut >= start;
  });
});

onBeforeMount(() => {
  fetchBookings();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
        {{ t('TDF.RESERVATIONS.HEADER') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ t('TDF.RESERVATIONS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Map Period Selector -->
    <div class="flex gap-2">
      <button
        v-for="days in [7, 14, 21, 31]"
        :key="days"
        class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
        :class="
          mapDays === days
            ? 'bg-blue-500 text-white'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
        "
        @click="mapDays = days"
      >
        {{ days }} {{ t('TDF.RESERVATIONS.DAYS') }}
      </button>
    </div>

    <!-- Reservations Table -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-slate-500">{{ t('TDF.COMMON.LOADING') }}</span>
    </div>

    <div v-else class="overflow-x-auto">
      <table
        class="w-full rounded-lg border border-slate-200 text-sm dark:border-slate-600"
      >
        <thead>
          <tr class="bg-slate-50 dark:bg-slate-700">
            <th
              class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              ID
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.RESERVATIONS.GUEST') }}
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              Check-in
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              Check-out
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.RESERVATIONS.NIGHTS') }}
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              Status
            </th>
            <th
              class="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="booking in bookingsInRange"
            :key="booking.id"
            class="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-750"
          >
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              #{{ booking.id }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ booking.contact?.name || booking.guest_name || '-' }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ formatDate(booking.check_in) }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ formatDate(booking.check_out) }}
            </td>
            <td
              class="px-3 py-2 text-center text-slate-700 dark:text-slate-200"
            >
              {{ booking.nights || '-' }}
            </td>
            <td class="px-3 py-2 text-center">
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="statusClass(booking.status)"
              >
                {{ statusLabel(booking.status) }}
              </span>
            </td>
            <td
              class="px-3 py-2 text-right font-medium text-slate-800 dark:text-slate-100"
            >
              {{ formatCurrency(booking.total_price) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      v-if="!isLoading && bookingsInRange.length === 0"
      class="py-4 text-center text-sm text-slate-500"
    >
      {{ t('TDF.RESERVATIONS.NO_RESERVATIONS') }}
    </p>
  </div>
</template>
